import React from 'react';
import { browserHistory } from 'react-router';
import queryString from 'query-string';

import AppRoutes from '../../routes/routes';
import AppStore from '../../stores/stores';
import LoginStore from '../../stores/login-store';
import AuthMixin from '../../mixins/authenticationMixin';
import { isFreeTrialExpired } from '../../middleware/middleware';
import SearchSuggestion from '../banners/banner-search-suggestion';
import AppConstants from '../../constants/constants';

import { GoSearch } from 'react-icons/go';
import AxonIcon from '../icons/axon-icon';

class MarketingSearchForm extends React.Component {
    constructor() {
        super();
        this.state = { searchValue: '', previousSearches: {}, isSearching: false, searchError: false, inputFocused: false };
        this.handleChange = this.handleChange.bind(this);
        this.resetSearch = this.resetSearch.bind(this);
        this.handleFocus = this.handleFocus.bind(this);
        this.handleBlur = this.handleBlur.bind(this);
    }

    handleFocus() {
        this.setState({ inputFocused: true });
    }

    handleBlur() {
        // Delay hiding the dropdown slightly to allow clicks on dropdown items to register
        setTimeout(() => {
            this.setState({ inputFocused: false });
        }, 150);
    }

    componentDidMount() {
        // Need to use refs or a specific class since there could be multiple search panels if old one exists
        document.addEventListener('click', this.resetSearch);
    }

    componentWillUnmount() {
        document.removeEventListener('click', this.resetSearch);
    }

    resetSearch(e) {
        if (e.target.closest('.marketing-search-suggestions') || e.target.tagName === 'A') {
            this.setState({ searchValue: '', isSearching: false, searchError: false });
        }
    }

    handleSearch(e) {
        e.preventDefault();
        const searchValue = this.state.searchValue;
        const userData = LoginStore.getSessionUserData();
        const modalQuery = queryString.stringify({ modal: 'upgrade' });

        // Check for free trial expiration
        if (isFreeTrialExpired(userData)) {
            return browserHistory.replace(`${AppRoutes.homepage}?${modalQuery}`);
        }

        // Route based on search mode
        if (this.props.searchMode === 'axon') {
            // AI Search mode - route to /chat with query
            const chatQuery = searchValue ? `?q=${encodeURIComponent(searchValue)}` : '';
            return browserHistory.push(`${AppRoutes.chat}${chatQuery}`);
        } else {
            // Database Search mode - existing behavior
            const searchQuery = queryString.stringify({
                q: searchValue || '*',
                page: AppConstants.DEFAULTSEARCHPAGE,
                per_page: AppConstants.PERPAGE,
            });
            return browserHistory.push(`${AppRoutes.search}?${searchQuery}`);
        }
    }

    handleChange(e) {
        const { userApiRequest } = this.props;
        const value = e.target.value;

        this.setState({ searchValue: value, searchError: false });

        if (value.length >= 3 && this.props.searchMode === 'database') {
            if (!this.state.previousSearches[value]) {
                this.setState({ isSearching: true, searchError: false });
            }

            AppStore.getAutocomplete(value, userApiRequest, undefined, {
                onSuccess: ({ data }) => {
                    const state = Object.assign({}, this.state.previousSearches);
                    state[value] = data;
                    this.setState({
                        previousSearches: state,
                        isSearching: false,
                        searchError: false
                    });
                },
                onError: error => {
                    this.setState({ isSearching: false, searchError: true });
                },
            });
        } else {
            this.setState({ isSearching: false, searchError: false });
        }
    }

    getData(target) {
        let results = this.state.previousSearches[this.state.searchValue] || null;
        if (!results) {
            return false;
        }

        let data = results[target] || null;

        if (!data || data.length === 0) {
            return false;
        }

        return data;
    }

    checklistsSuggestions() {
        let checklists = this.getData('checklists');
        if (checklists) {
            return <SearchSuggestion {...this.state} {...this.props} data={checklists} title="Checklists" />;
        } else {
            return null;
        }
    }

    topicsSuggestions() {
        let topics = this.getData('topics');
        if (topics) {
            return <SearchSuggestion {...this.state} {...this.props} data={topics} title="Topics" />;
        } else {
            return null;
        }
    }

    chaptersSuggestions() {
        let chapters = this.getData('chapters');
        if (chapters) {
            return <SearchSuggestion {...this.state} {...this.props} data={chapters} title="Chapters" />;
        } else {
            return null;
        }
    }

    render() {
        let checklists = null,
            topics = null,
            chapters = null;
        let searchLength = this.state.searchValue.length;
        let hasData = false;
        let hasCachedData = false;

        if (searchLength > 2) {
            checklists = this.checklistsSuggestions();
            topics = this.topicsSuggestions();
            chapters = this.chaptersSuggestions();
            hasData = !!(checklists || topics || chapters);
            hasCachedData = !!this.state.previousSearches[this.state.searchValue];
        }

        const isCompact = this.props.compact;
        const showDropdown = this.props.searchMode === 'database' &&
            searchLength > 2 &&
            (hasData || this.state.isSearching || this.state.searchError || hasCachedData) &&
            this.state.inputFocused;

        return (
            <div className={isCompact ? 'marketing-search marketing-search-compact' : 'marketing-search marketing-search-hero'} style={{ position: 'relative', width: '100%', maxWidth: isCompact ? 'none' : '880px', margin: '0 auto' }}>
                <form
                    onSubmit={this.handleSearch.bind(this)}
                    className="marketing-search-form"
                >
                    {this.props.searchMode === 'axon' ? (
                        <div className="marketing-search-icon">
                            <AxonIcon size={isCompact ? 18 : 26} color="#6B7280" />
                        </div>
                    ) : (
                        <GoSearch className="marketing-search-icon-svg" />
                    )}
                    <input
                        placeholder={
                            isCompact
                                ? "Search..."
                                : this.props.searchMode === 'axon'
                                    ? "Ask Axon anything about neurology..."
                                    : "Disorder, Symptom or Disease"
                        }
                        onChange={this.handleChange}
                        onFocus={this.handleFocus}
                        onBlur={this.handleBlur}
                        type="search"
                        value={this.state.searchValue}
                        required={true}
                        className="marketing-search-input"
                    />
                    <button type="submit" className="btn btn-hero-primary marketing-search-submit">
                        {this.props.searchMode === 'axon' ? 'Ask' : 'Search'}
                    </button>
                </form>

                {showDropdown && (
                    <div className="marketing-search-suggestions" style={{
                        position: 'absolute',
                        top: 'calc(100% + 12px)',
                        left: 0,
                        right: 0,
                        background: '#fff',
                        borderRadius: '16px',
                        padding: '16px 20px',
                        boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 8px 10px -6px rgba(0,0,0,0.1)',
                        border: '1px solid #e5e7eb',
                        zIndex: 50,
                        textAlign: 'left',
                        maxHeight: '440px',
                        overflowY: 'auto'
                    }}>
                        {this.state.isSearching ? (
                            <div style={{ padding: '16px 0', textAlign: 'center', color: '#6B7280', fontSize: '14.5px', fontWeight: 500 }}>
                                    <div style={{ display: 'inline-block', width: '16px', height: '16px', border: '2px solid #E5E7EB', borderTopColor: '#3B82F6', borderRadius: '50%', animation: 'spin 1s linear infinite', marginRight: '8px', verticalAlign: 'middle', marginTop: '-2px' }} />
                                    Searching...
                            </div>
                        ) : this.state.searchError ? (
                            <div style={{ padding: '16px 0', textAlign: 'center', color: '#EF4444', fontSize: '14.5px', fontWeight: 500 }}>
                                Unable to load results. Please try again.
                            </div>
                        ) : !hasData && hasCachedData ? (
                            <div style={{ padding: '16px 0', textAlign: 'center', color: '#6B7280', fontSize: '14.5px', fontWeight: 500 }}>
                                No results found for "{this.state.searchValue}"
                            </div>
                        ) : (
                            <React.Fragment>
                                {checklists}
                                {topics}
                                {chapters}
                            </React.Fragment>
                        )}
                        <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                    </div>
                )}
            </div>
        );
    }
}

export default AuthMixin(MarketingSearchForm);
